'use client';

import { cn } from '@/lib/cn';
import { DrawerContent, DrawerTitle } from '@heidi/design-system/drawer';
import {
  DropdownMenuContent,
  type DropdownMenuContentProps,
} from '@heidi/design-system/dropdown-menu';
import * as React from 'react';

import { VariantContext } from './context';

type AdaptiveMenuContentProps = Omit<DropdownMenuContentProps, 'title'> & {
  /** Accessible drawer title (visually hidden); required for a11y on mobile. */
  title: React.ReactNode;
};

export const AdaptiveMenuContent = React.forwardRef<
  HTMLDivElement,
  AdaptiveMenuContentProps
>(({ children, className, title, side, align, onClick, ...props }, ref) => {
  const variant = React.useContext(VariantContext);

  if (variant === 'menu') {
    return (
      <DropdownMenuContent
        ref={ref}
        side={side}
        align={align}
        className={className}
        onClick={onClick}
        {...props}
      >
        {children}
      </DropdownMenuContent>
    );
  }

  return (
    <DrawerContent>
      <DrawerTitle className="sr-only">{title}</DrawerTitle>
      {/* min-h-0 + flex-1 lets this scroll within DrawerContent's max-h-[90%] */}
      <div
        role="menu"
        className={cn('min-h-0 flex-1 overflow-y-auto p-1 pb-4', className)}
        onClick={onClick}
        data-testid={props['data-testid']}
      >
        {children}
      </div>
    </DrawerContent>
  );
});
AdaptiveMenuContent.displayName = 'AdaptiveMenuContent';
